/**
 * Security utilities for input validation and sanitization
 * Prevents XSS, SQL injection, and other security vulnerabilities
 */

const ALLOWED_PATTERNS = {
	name: /^[a-zA-Z.'-]+(?: [a-zA-Z.'-]+)*$/,
	letters: /^[a-zA-Z\s]+$/,
	phone: /^04\d{8}$/,
	email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9][a-zA-Z0-9.-]*.[a-zA-Z]{2,}$/,
	address: /^[a-zA-Z0-9\s\-\,\.\#\/]+$/g,
	textarea: /^[a-zA-Z0-9\s\-\,\.\!\?\'\"\(\)\@\+]+$/,
	numeric: /^[0-9]+/,
	postcode: /^[0-9]{4}/,
	alphanumeric: /^[a-zA-Z0-9]+$/,
	decimal: /^\d{0,3}(\.\d{0,10})?$/,
};

export function sanitizeInput(input: string, type: keyof typeof ALLOWED_PATTERNS): string {
	if (!input || typeof input !== "string") return "";

	let sanitized = input;

	try {
		// Required:Remove all HTML Tags
		sanitized = sanitized.replace(/<|>/g, "");

		// Remove characters according to the type regex pattern
		sanitized = sanitized.replace(type, "");

		if (type === "numeric" || type === "postcode") {
			sanitized = sanitized.replace(/[^0-9]/g, "");
		}

		if (type === "postcode") {
			sanitized = sanitized.slice(0, 4);
		}

		// Remove null bytes and control characters
		sanitized = sanitized.replace(/[\x00-\x1F\x7F]/g, "");
	} catch (error) {
		console.error(error);
	}

	return sanitized;
}

/**
 * Validates input against allowed patterns
 */
export function validateInput(input: string, type: keyof typeof ALLOWED_PATTERNS): boolean {
	if (!input || typeof input !== "string") return false;

	const pattern = ALLOWED_PATTERNS[type];
	const result = pattern.test(input);
	return result;
}

/**
 * Comprehensive input security check
 */
export function isInputSecure(
	input: string,
	type: keyof typeof ALLOWED_PATTERNS,
	required: boolean = true,
	maxLength: number = 150,
): {
	isValid: boolean;
	sanitized: string;
} {
	if (required && (!input || typeof input !== "string")) {
		return { isValid: false, sanitized: "" };
	} else if (!required && !input) {
		return { isValid: true, sanitized: "" };
	}

	// Sanitize the input
	const sanitized = sanitizeInput(input, type);

	const isValidValue = validateInput(sanitized, type);
	const isValidLength = sanitized.length <= maxLength;

	const isValid = isValidValue && isValidLength;

	return {
		isValid,
		sanitized,
	};
}

/**
 * Specific validators for form fields
 */
export const secureInput = {
	name: (input: string) => isInputSecure(input, "name"),
	email: (input: string) => isInputSecure(input, "email"),
	phone: (input: string) => isInputSecure(input, "phone"),
	address: (input: string) => isInputSecure(input, "address"),
	textarea: (input: string) => isInputSecure(input, "textarea", false, 250),
	numeric: (input: string) => isInputSecure(input, "numeric"),
	postcode: (input: string) => isInputSecure(input, "postcode", true, 4),
	alphanumeric: (input: string) => isInputSecure(input, "alphanumeric"),
	rate: (input: string) => isInputSecure(input, "decimal"),
};
